import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type SharpenMode = "sharpen" | "sharpen-more" | "unsharp-mask";

export interface SharpenParams {
  mode: SharpenMode;
  amount: number;
  radius: number;
  threshold: number;
}

const SHARPEN_KERNEL = [
  [0, -1, 0],
  [-1, 5, -1],
  [0, -1, 0],
];

const SHARPEN_MORE_KERNEL = [
  [-1, -1, -1],
  [-1, 9, -1],
  [-1, -1, -1],
];

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

function applySharpenKernel(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  kernel: number[][],
): void {
  const n = w * h;
  for (let i = 0; i < n; i++) {
    const y = Math.floor(i / w);
    const x = i % w;
    const o = i * 4;
    let r = 0;
    let g = 0;
    let b = 0;
    for (let ky = 0; ky < 3; ky++) {
      let sy = y + ky - 1;
      if (sy < 0) sy = 0;
      else if (sy >= h) sy = h - 1;
      for (let kx = 0; kx < 3; kx++) {
        let sx = x + kx - 1;
        if (sx < 0) sx = 0;
        else if (sx >= w) sx = w - 1;
        const ko = (sy * w + sx) * 4;
        const kv = kernel[ky]![kx]!;
        r += src[ko]! * kv;
        g += src[ko + 1]! * kv;
        b += src[ko + 2]! * kv;
      }
    }
    dst[o] = clamp8(r);
    dst[o + 1] = clamp8(g);
    dst[o + 2] = clamp8(b);
    dst[o + 3] = src[o + 3]!;
  }
}

function buildGaussianKernel(sigma: number): Float32Array {
  if (!(sigma > 0)) return new Float32Array([1]);
  const half = Math.max(1, Math.ceil(3 * sigma));
  const size = 2 * half + 1;
  const kernel = new Float32Array(size);
  const twoSigmaSq = 2 * sigma * sigma;
  let sum = 0;
  for (let i = 0; i < size; i++) {
    const x = i - half;
    const v = Math.exp(-(x * x) / twoSigmaSq);
    kernel[i] = v;
    sum += v;
  }
  const inv = 1 / sum;
  for (let i = 0; i < size; i++) kernel[i] = kernel[i]! * inv;
  return kernel;
}

function blurRgba(
  src: Uint8ClampedArray,
  w: number,
  h: number,
  radius: number,
): Uint8ClampedArray {
  if (!(radius > 0) || w === 0 || h === 0) return new Uint8ClampedArray(src);

  const sigma = radius / 3;
  const kernel = buildGaussianKernel(sigma);
  if (kernel.length === 1) return new Uint8ClampedArray(src);

  const n = w * h;
  const pre = new Float32Array(w * h * 4);
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const a = src[o + 3]! / 255;
    pre[o] = src[o]! * a;
    pre[o + 1] = src[o + 1]! * a;
    pre[o + 2] = src[o + 2]! * a;
    pre[o + 3] = src[o + 3]!;
  }

  const tmp = new Float32Array(w * h * 4);
  const half = (kernel.length - 1) >> 1;

  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      let r = 0, g = 0, b = 0, a = 0;
      for (let k = 0; k < kernel.length; k++) {
        let sx = x + k - half;
        if (sx < 0) sx = 0;
        else if (sx >= w) sx = w - 1;
        const o = (row + sx) * 4;
        const wk = kernel[k]!;
        r += pre[o]! * wk;
        g += pre[o + 1]! * wk;
        b += pre[o + 2]! * wk;
        a += pre[o + 3]! * wk;
      }
      const o = (row + x) * 4;
      tmp[o] = r; tmp[o + 1] = g; tmp[o + 2] = b; tmp[o + 3] = a;
    }
  }

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let r = 0, g = 0, b = 0, a = 0;
      for (let k = 0; k < kernel.length; k++) {
        let sy = y + k - half;
        if (sy < 0) sy = 0;
        else if (sy >= h) sy = h - 1;
        const o = (sy * w + x) * 4;
        const wk = kernel[k]!;
        r += tmp[o]! * wk;
        g += tmp[o + 1]! * wk;
        b += tmp[o + 2]! * wk;
        a += tmp[o + 3]! * wk;
      }
      const o = (y * w + x) * 4;
      pre[o] = r; pre[o + 1] = g; pre[o + 2] = b; pre[o + 3] = a;
    }
  }

  const out = new Uint8ClampedArray(w * h * 4);
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    const a = pre[o + 3]!;
    if (a <= 0) { out[o] = 0; out[o + 1] = 0; out[o + 2] = 0; out[o + 3] = 0; continue; }
    const inv = 255 / a;
    out[o] = Math.round(pre[o]! * inv);
    out[o + 1] = Math.round(pre[o + 1]! * inv);
    out[o + 2] = Math.round(pre[o + 2]! * inv);
    out[o + 3] = Math.round(a);
  }
  return out;
}

function applyUnsharpMask(
  src: Uint8ClampedArray,
  blurred: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  amount: number,
  threshold: number,
): void {
  const n = w * h;
  const amountFactor = amount / 100;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    for (let c = 0; c < 3; c++) {
      const orig = src[o + c]!;
      const blr = blurred[o + c]!;
      const diff = orig - blr;
      if (Math.abs(diff) > threshold) {
        dst[o + c] = clamp8(Math.round(orig + amountFactor * diff));
      } else {
        dst[o + c] = orig;
      }
    }
    dst[o + 3] = src[o + 3]!;
  }
}

export const SharpenSuite: Filter<SharpenParams> = {
  id: "sharpen-suite",
  displayName: "Sharpen",
  defaultParams: { mode: "sharpen", amount: 100, radius: 1, threshold: 0 },

  apply(
    input: PixelBuffer,
    params: SharpenParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) { dst.set(src); return out; }

    switch (params.mode) {
      case "sharpen":
        applySharpenKernel(src, dst, w, h, SHARPEN_KERNEL);
        break;
      case "sharpen-more":
        applySharpenKernel(src, dst, w, h, SHARPEN_MORE_KERNEL);
        break;
      case "unsharp-mask": {
        const blurred = blurRgba(src, w, h, params.radius);
        applyUnsharpMask(src, blurred, dst, w, h, params.amount, params.threshold);
        break;
      }
    }
    return out;
  },
};
